<!-- ISO Selection Modal -->
@if (showIsoModal) {
  <div class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" (click)="closeIsoModal($event)" tabindex="0" (keydown.enter)="closeIsoModal($event)">
    <div class="bg-white rounded-2xl max-w-2xl w-full shadow-2xl transform transition-all animate-in">
      <!-- Header -->
      <div class="p-6 border-b border-gray-200">
        <h2 class="text-2xl font-bold text-gray-900 text-center">
          Choose Your Assessment
        </h2>
        <p class="text-gray-600 text-center mt-1">
          Select the certification standard you'd like to assess.
        </p>
      </div>

      <!-- Body -->
      <div class="p-6 space-y-4">
        <!-- Option 1: ISO 27001 -->
        <div 
          class="border-2 border-gray-200 rounded-xl p-6 hover:border-blue-400 transition-all cursor-pointer"
          [class.border-blue-500]="selectedIso === '27001'"
          (click)="selectIso('27001')" tabindex="0" (keydown.enter)="selectIso('27001')"
        >
          <div class="flex items-start gap-4">
            <div class="flex-1">
              <h3 class="text-lg font-semibold text-gray-900">ISO/IEC 27001</h3>
              <p class="text-sm text-gray-600">Information Security Management System</p>
            </div>
            <div 
              class="w-5 h-5 rounded-full border-2 flex-shrink-0 mt-1"
              [class.border-blue-500]="selectedIso === '27001'"
              [class.bg-blue-500]="selectedIso === '27001'"
            >
              @if (selectedIso === '27001') { <svg class="w-4 h-4 text-white m-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"></path>
              </svg> }
            </div>
          </div>
        </div>

        <!-- Option 2: ISO 42001 -->
        <div 
          class="border-2 border-gray-200 rounded-xl p-6 hover:border-purple-400 transition-all cursor-pointer"
          [class.border-purple-500]="selectedIso === '42001'"
          (click)="selectIso('42001')" tabindex="0" (keydown.enter)="selectIso('42001')"
        >
          <div class="flex items-start gap-4">
            <div class="flex-1">
              <h3 class="text-lg font-semibold text-gray-900">ISO/IEC 42001</h3>
              <p class="text-sm text-gray-600">AI Management System</p>
            </div>
            <div 
              class="w-5 h-5 rounded-full border-2 flex-shrink-0 mt-1"
              [class.border-purple-500]="selectedIso === '42001'"
              [class.bg-purple-500]="selectedIso === '42001'"
            >
              @if (selectedIso === '42001') { <svg class="w-4 h-4 text-white m-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"></path>
              </svg> }
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="p-6 border-t border-gray-200 flex justify-end gap-3">
        <button
          class="text-gray-500 hover:text-gray-700 font-medium"
          (click)="closeIsoModal()"
        >
          Cancel
        </button>
        <button
          class="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          [disabled]="!selectedIso"
          (click)="confirmIsoSelection()"
        >
          Continue
        </button>
      </div>
    </div>
  </div>
}

<div class="min-h-screen bg-gradient-to-br from-blue-300 via-white to-white">
  <!-- Navbar -->
  <nav class="top-0 w-full z-50">
    <div class="max-w-6xl mx-auto px-6 py-6 flex items-center">
      <div class="cursor-pointer" routerLink="/">
        <img src="assets/logo.png" alt="AuditFixers Logo" class="h-20 w-auto" />
      </div>
    </div>
  </nav>

  <!-- Page Content -->
  <div class="flex flex-col justify-start items-center pb-12 px-6">
    <form class="w-full max-w-4xl mx-auto bg-white/90 p-8 rounded-2xl shadow-xl backdrop-blur-sm">

      <!-- ISO Badge - Shows selected standard -->
      @if (selectedIso) {
        <div class="flex justify-center mb-4">
          <span 
            class="inline-block px-4 py-1 rounded-full text-sm font-semibold"
            [class.bg-blue-100]="selectedIso === '27001'"
            [class.text-blue-700]="selectedIso === '27001'"
            [class.bg-purple-100]="selectedIso === '42001'"
            [class.text-purple-700]="selectedIso === '42001'"
          >
            {{ selectedIso === '27001' ? 'ISO/IEC 27001:2022 - Information Security' : 'ISO/IEC 42001:2023 - AI Management' }}
          </span>
        </div>
      }

      <h1 class="text-3xl font-bold mb-2 text-gray-900 text-center">
        Quick Gap Check
      </h1>

      <p class="text-sm text-gray-600 mb-6 text-center">
        It only takes 10 minutes to complete the assessment.
      </p>

      <!-- ISO Selection Button -->
      <div class="mb-6">
        <button
          type="button"
          (click)="openIsoModal()"
          class="w-full border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-blue-400 hover:bg-blue-50 transition-all text-center"
        >
          <div class="flex items-center justify-center gap-2">
            @if (selectedIso) {
              <span class="text-green-600">✓</span>
              <span class="text-gray-700">
                Selected: {{ selectedIso === '27001' ? 'ISO/IEC 27001' : 'ISO/IEC 42001' }}
              </span>
              <span class="text-blue-600 text-sm">(Click to change)</span>
            } @else {
              <span class="text-gray-500">Click to select ISO standard *</span>
            }
          </div>
        </button>
        @if (!selectedIso && showIsoError) {
          <div class="text-red-600 text-sm mt-1">
            Please select an ISO standard before proceeding.
          </div>
        }
      </div>

      <h2 class="text-lg font-semibold text-gray-800 mb-4">
        Company Information
      </h2>

      <fieldset class="mb-6">

        <!-- Company Name -->
        <div class="flex flex-col mb-4">
          <label class="mb-1 text-gray-600 font-medium" for="companyName">
            Company Name *
          </label>

          <input
            id="companyName"
            type="text"
            placeholder="e.g. AuditFixers Ltd"
            (input)="onCompanyInput($event)"
            [(ngModel)]="customer.companyName"
            name="companyName"
            autocomplete="organization"
            class="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />

          @if (errors['companyName']) {
            <div class="text-red-600 text-sm mt-1">
              {{ errors['companyName'] }}
            </div>
          }
        </div>

        <!-- Representative Name -->
        <div class="flex flex-col mb-4 text-sm">
          <label class="mb-1 text-gray-600 font-medium" for="name">
            Representative Name *
          </label>

          <input
            id="name"
            type="text"
            placeholder="e.g. John Murphy"
            (input)="onNameInput($event)"
            [(ngModel)]="customer.name"
            name="name"
            autocomplete="name"
            class="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />

          @if (errors['name']) {
            <div class="text-red-600 text-sm mt-1">
              {{ errors['name'] }}
            </div>
          }
        </div>

        <!-- Contact Number -->
        <div class="flex flex-col mb-4 text-sm">
          <label class="mb-1 text-gray-600 font-medium" for="contactNumber">
            Contact Number *
          </label>

          <input
            id="contactNumber"
            type="tel"
            placeholder="+353 XX XXX XXXX"
            [(ngModel)]="customer.contactNumber"
            (input)="onMobileInput($event)"
            name="contactNumber"
            autocomplete="tel"
            maxlength="16"
            class="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />

          @if (errors['contactNumber']) {
            <div class="text-red-600 text-sm mt-1">
              {{ errors['contactNumber'] }}
            </div>
          }
        </div>

        <!-- Email -->
        <div class="flex flex-col mb-4 text-sm">
          <label class="mb-1 text-gray-600 font-medium" for="email">
            Email *
          </label>

          <input
            id="email"
            type="email"
            placeholder="e.g. john@company.com"
            (input)="onEmailInput($event)"
            [(ngModel)]="customer.email"
            name="email"
            autocomplete="email"
            class="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />

          @if (errors['email']) {
            <div class="text-red-600 text-sm mt-1">
              {{ errors['email'] }}
            </div>
          }
        </div>

      </fieldset>

      <button
        type="button"
        [disabled]="hasErrors || !selectedIso"
        (click)="beginAssessment()"
        class="w-full bg-blue-600 text-white px-6 py-3 rounded-lg transition transform hover:scale-105 hover:bg-blue-700 disabled:bg-gray-400 disabled:text-gray-200 disabled:cursor-not-allowed cursor-pointer text-sm"
      >
        Begin Assessment
      </button>

    </form>
  </div>
</div>

<style>
.animate-in {
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.border-2:hover {
  transition: all 0.2s ease;
}
</style>